import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import FacebookLoginButton from '../../../components/WhatsApp/FacebookLoginButton';
import { WhatsAppCarousel } from '../components/ui';
import useFBAuthenticated from '../../../hooks/useFBAuthenticated';
import { useWhatsAppData } from '../../../hooks/useWhatsAppData';
import GettingStarted from './GettingStarted';
import '../styles/WhatsAppNew.scss';

const WhatsAppNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { access_token, role } = useSelector((state) => state.auth.user);
  const { isAuthenticated } = useFBAuthenticated(access_token);
  const { data: waCombinedData, mutate: refetchWhatsAppData, isLoading } = useWhatsAppData(
    'get-combined-data',
    access_token?.token
  );
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [, setError] = useState(null);
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const [showMainInterface, setShowMainInterface] = useState(false);

  // Determine if user is manager
  const isManager = useMemo(() => role === 'manager', [role]);

  // Determine base path based on user role
  const basePath = useMemo(() => isManager ? '/manager/dashboard/whatsapp' : '/dashboard/whatsapp', [isManager]);

  // Stable navigation function to prevent re-renders
  const navigateToWhatsApp = useCallback((path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (isManager) {
      // For managers, navigate to phone number details when configured
      if ((isAuthenticated || isLoggedIn) && !isLoading && waCombinedData) {
        const firstItem = waCombinedData?.data?.combinedData?.[0];
        const hasWhatsAppConfig = Boolean(
          firstItem?.waba_id &&
            firstItem.phoneNumbers?.data?.length > 0 &&
            firstItem.phoneNumbers?.data[0]?.id
        );

        if (hasWhatsAppConfig) {
          // Navigate to phone number details page
          const whatsAppPath = `${basePath}/phone-number-details/${firstItem.waba_id}/${firstItem.phoneNumbers.data[0].id}`;
          navigateToWhatsApp(whatsAppPath);
        } else if (waCombinedData?.data?.wabaToken) {
          setShowGettingStarted(true);
          setShowMainInterface(false);
        } else {
          setShowGettingStarted(false);
          setShowMainInterface(false);
        }
      }
    } else {
      // For regular users, keep the navigation logic
      if ((isAuthenticated || isLoggedIn) && !isLoading && waCombinedData) {
        const firstItem = waCombinedData?.data?.combinedData?.[0];
        const hasWhatsAppConfig = Boolean(
          firstItem?.waba_id &&
            firstItem.phoneNumbers?.data?.length > 0 &&
            firstItem.phoneNumbers?.data[0]?.id
        );

        let whatsAppPath = basePath;

        if (hasWhatsAppConfig) {
          whatsAppPath = `${basePath}/phone-number-details/${firstItem.waba_id}/${firstItem.phoneNumbers.data[0].id}`;
        } else if (waCombinedData?.data?.wabaToken) {
          whatsAppPath = `${basePath}/getting-started`;
        }

        navigateToWhatsApp(whatsAppPath);
      }
    }
  }, [isAuthenticated, isLoggedIn, waCombinedData, isLoading, basePath, navigateToWhatsApp, isManager]);

  const handleLoginSuccess = useCallback(async () => {
    try {
      setIsLoggedIn(true);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  return (
    <div className="whatsapp-new-page has-sidebar-secondary">
      <div className="whatsapp-new-container">
        {!showGettingStarted && !showMainInterface && (
          <>
            <div className="whatsapp-blue-section">
              <div className="banner-content">
                <div className="banner-text">
                  A few clicks away from sending messages to thousands of WhatsApp users
                </div>
                <WhatsAppCarousel />
              </div>
            </div>

            <div className="whatsapp-main-section">
              <div className="content-wrapper">
                <h1 className="main-title">Let's Get Started with WhatsApp Business API</h1>
                <p className="main-description">
                  Integrate WhatsApp Business Platform (API) with Facebook to facilitate effortless
                  customer engagement and support.
                </p>

                {!isLoggedIn && !isAuthenticated && (
                  <FacebookLoginButton
                    accessToken={access_token}
                    onLoginSuccess={handleLoginSuccess}
                    buttonText="Connect with Facebook"
                    style={{ width: '300px' }}
                    isGettingStarted={true}
                  />
                )}
              </div>

              <div className="mascot-container"></div>
            </div>
          </>
        )}

        {showGettingStarted && (
          <GettingStarted
            isManager={isManager}
            onComplete={() => {
              setShowGettingStarted(false);
              setShowMainInterface(true);
            }}
          />
        )}

        {showMainInterface && (
          <div className="main-interface-content">
            <h2>WhatsApp Dashboard</h2>
            <p>Your WhatsApp is configured and ready to use.</p>
            {/* Placeholder for main interface; can be enhanced later */}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(WhatsAppNew);
